import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { 
  Megaphone, Plus, Edit, Trash2, Send, Clock, Users, FileText,
  GraduationCap, AlertTriangle, CheckCircle, X, RefreshCw, Paperclip,
  Download, BookOpen, Upload
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';

const CfaAdminAnnouncementsPage = ({ onMenuClick }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  
  // Form data
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [priority, setPriority] = useState('normal');
  const [targetType, setTargetType] = useState('all');
  const [targetGroupId, setTargetGroupId] = useState('');
  const [targetCourseId, setTargetCourseId] = useState('');
  const [attachment, setAttachment] = useState(null);
  
  // Reference data
  const [groups, setGroups] = useState([]);
  const [courses, setCourses] = useState([]);

  // Track which announcements are expanded (show full content)
  const [expandedIds, setExpandedIds] = useState({});

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  useEffect(() => {
    fetchAnnouncements();
    fetchReferenceData();
  }, []);

  useEffect(() => {
    if (searchParams.get('action') === 'create') {
      setShowCreateModal(true);
      // Replace (not push) so the Back button returns to the dashboard, not this modal.
      setSearchParams({}, { replace: true });
    }
  }, [searchParams]);

  const fetchAnnouncements = async () => {
    try {
      const response = await api.get('/announcements/admin');
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Erreur chargement annonces:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReferenceData = async () => {
    try {
      const [groupsRes, coursesRes] = await Promise.all([
        api.get('/groups'),
        api.get('/content/courses')
      ]);
      setGroups(groupsRes.data);
      setCourses(coursesRes.data);
    } catch (error) {
      console.error('Error fetching reference data:', error);
    }
  };

  const resetForm = () => {
    setTitle('');
    setContent('');
    setPriority('normal');
    setTargetType('all');
    setTargetGroupId('');
    setTargetCourseId('');
    setAttachment(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!title || !content) {
      toast.error('Veuillez remplir le titre et le contenu');
      return;
    }

    setSaving(true);
    
    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('content', content);
      formData.append('priority', priority);
      
      // Target configuration
      if (targetType === 'student' || targetType === 'trainer') {
        formData.append('target_role', targetType);
      } else if (targetType === 'group' && targetGroupId) {
        formData.append('target_group_id', targetGroupId);
      } else if (targetType === 'course' && targetCourseId) {
        formData.append('target_course_id', targetCourseId);
      }
      
      // Attachment
      if (attachment) {
        formData.append('attachment', attachment);
      }

      const response = await api.post('/announcements', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`Annonce envoyée à ${response.data.notified_users} utilisateur(s)`);
      setShowCreateModal(false);
      resetForm();
      fetchAnnouncements();
    } catch (error) {
      console.error('Erreur création annonce:', error);
      toast.error("Erreur lors de l'envoi de l'annonce");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette annonce ?')) return;
    
    try {
      await api.delete(`/announcements/${id}`);
      toast.success('Annonce supprimée');
      fetchAnnouncements();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const getPriorityBadge = (priority) => {
    const config = {
      urgent: { label: 'Urgent', className: 'bg-red-100 text-red-700 border-red-200' },
      important: { label: 'Important', className: 'bg-amber-100 text-amber-700 border-amber-200' },
      normal: { label: 'Normal', className: 'bg-slate-100 text-slate-700 border-slate-200' }
    };
    const { label, className } = config[priority] || config.normal;
    return <Badge variant="outline" className={className}>{label}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' o';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' Ko';
    return (bytes / (1024 * 1024)).toFixed(1) + ' Mo';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
        <Header title="Annonces" onMenuClick={onMenuClick} />
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header title="Annonces" onMenuClick={onMenuClick} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
              <Megaphone className="w-6 h-6 text-violet-600" />
              Gestion des Annonces
            </h2>
            <p className="text-slate-600 dark:text-slate-400">
              {announcements.length} annonce(s) publiée(s)
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={fetchAnnouncements} title="Actualiser">
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button onClick={() => setShowCreateModal(true)} className="bg-violet-600 hover:bg-violet-700">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle annonce
            </Button>
          </div>
        </div>

        {/* Announcements List */}
        <div className="space-y-4">
          {announcements.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Megaphone className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">Aucune annonce</h3>
                <p className="text-slate-500 mb-4">Créez votre première annonce pour informer vos utilisateurs</p>
                <Button onClick={() => setShowCreateModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Créer une annonce
                </Button>
              </CardContent>
            </Card>
          ) : (
            announcements.map((announcement) => (
              <Card key={announcement.id} className={`${!announcement.is_active ? 'opacity-60' : ''}`}>
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-start gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                          {announcement.title}
                        </h3>
                        {getPriorityBadge(announcement.priority)}
                        {!announcement.is_active && (
                          <Badge variant="outline" className="bg-slate-100">Désactivée</Badge>
                        )}
                      </div>
                      
                      <p className={`text-slate-600 dark:text-slate-400 whitespace-pre-wrap ${expandedIds[announcement.id] ? 'mb-2' : 'mb-2 line-clamp-2'}`}>
                        {announcement.content}
                      </p>
                      {announcement.content && announcement.content.length > 120 && (
                        <button
                          type="button"
                          onClick={() => toggleExpanded(announcement.id)}
                          className="text-sm font-medium text-violet-600 hover:text-violet-700 mb-4"
                        >
                          {expandedIds[announcement.id] ? 'Réduire' : 'Lire la suite'}
                        </button>
                      )}
                      
                      {/* Attachment */}
                      {announcement.attachment_url && (
                        <div className="flex items-center gap-2 mb-4 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg w-fit">
                          <Paperclip className="w-4 h-4 text-violet-600" />
                          <span className="text-sm text-slate-700 dark:text-slate-300">
                            {announcement.attachment_name}
                          </span>
                          <span className="text-xs text-slate-500">
                            ({formatFileSize(announcement.attachment_size)})
                          </span>
                          <a 
                            href={`${process.env.REACT_APP_BACKEND_URL}${announcement.attachment_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-2 text-violet-600 hover:text-violet-700"
                          >
                            <Download className="w-4 h-4" />
                          </a>
                        </div>
                      )}
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {announcement.target_info}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {formatDate(announcement.created_at)}
                        </span>
                        <span>
                          Par {announcement.author_name}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleDelete(announcement.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </main>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-violet-600" />
              Nouvelle annonce
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label>Titre *</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Titre de l'annonce"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Contenu *</Label>
              <Textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Contenu de l'annonce..."
                rows={4}
                className="mt-1"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Priorité</Label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="w-full mt-1 border border-slate-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="normal">Normal</option>
                  <option value="important">Important</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              
              <div>
                <Label>Destinataires</Label>
                <select
                  value={targetType}
                  onChange={(e) => {
                    setTargetType(e.target.value);
                    setTargetGroupId('');
                    setTargetCourseId('');
                  }}
                  className="w-full mt-1 border border-slate-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="all">Tous les utilisateurs</option>
                  <option value="student">Étudiants uniquement</option>
                  <option value="trainer">Formateurs uniquement</option>
                  <option value="group">Groupe spécifique</option>
                  <option value="course">Cours spécifique</option>
                </select>
              </div>
            </div>
            
            {/* Group selector */}
            {targetType === 'group' && (
              <div>
                <Label>Sélectionner le groupe</Label>
                <select
                  value={targetGroupId}
                  onChange={(e) => setTargetGroupId(e.target.value)}
                  className="w-full mt-1 border border-slate-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="">-- Choisir un groupe --</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>{group.name}</option>
                  ))}
                </select>
              </div>
            )}
            
            {/* Course selector */}
            {targetType === 'course' && (
              <div>
                <Label>Sélectionner le cours</Label>
                <select
                  value={targetCourseId}
                  onChange={(e) => setTargetCourseId(e.target.value)}
                  className="w-full mt-1 border border-slate-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="">-- Choisir un cours --</option>
                  {courses.map((course) => (
                    <option key={course.id} value={course.id}>{course.name}</option>
                  ))}
                </select>
              </div>
            )}
            
            {/* Attachment */}
            <div>
              <Label>Pièce jointe (optionnel)</Label>
              <div className="mt-1 flex items-center gap-2">
                <Input
                  ref={fileInputRef}
                  type="file"
                  onChange={(e) => setAttachment(e.target.files?.[0] || null)}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png"
                />
              </div>
              {attachment && (
                <p className="text-xs text-slate-500 mt-1">
                  {attachment.name} ({formatFileSize(attachment.size)})
                </p>
              )}
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => {
                setShowCreateModal(false);
                resetForm();
              }}>
                Annuler
              </Button>
              <Button type="submit" disabled={saving} className="bg-violet-600 hover:bg-violet-700">
                {saving ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Envoi...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Envoyer l'annonce
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CfaAdminAnnouncementsPage;
